<!-- eslint-disable prettier/prettier -->
<template>
  <v-container>
    <avs-backButton
      v-if="!inDialog"
      backTo="NonConformities"
    />
    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.informations]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.informations">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="2"
          :rules="[() => isFormValid.pictures]"
        >
          {{ $tc("commons.picture", 2) }}
          <small v-show="!isFormValid.pictures">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="3"
          :rules="[() => isFormValid.causeAndResolution]"
        >
          {{ $t("commons.cause") }}
          <small v-show="!isFormValid.causeAndResolution">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="4">
          {{ $t("commons.resolution") }}
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="5">{{ $tc("commons.signature", 2) }}</AvsStep>
      </template>
      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-informations"
            v-model="isFormValid.informations"
            :readonly="isReadOnly"
            class="pt-2 mb-5"
            lazy-validation
          >
            <InspectionInput
              v-model="nonConformity.inspection"
              :clearable="!isReadOnly"
              :disabled="!!inspection"
              :readonly="!!inspection"
              :rules="rules.inspection"
              dense
              outlined
            ></InspectionInput>

            <v-text-field
              v-model="nonConformity.nc_number"
              :rules="isDisable ? [] : rules.nc_number"
              :disabled="isDisable"
              auto-grow
              clearable
              dense
              outlined
              :label="$t('commons.nc_number')"
            />
            <div style="margin-bottom: 25px">
              <label>{{ $t("commons.description") }}</label>
              <ckeditor
                :editor="editor"
                :rules="rules.quick_description"
                :disabled="isDisable"
                tag-name="textarea"
                rows="4"
                v-model="nonConformity.quick_description"
                :config="editorConfig"
                :label="$t('commons.description')"
                class="content-element"
              ></ckeditor>
            </div>

            <v-combobox
              v-model="nonConformity.area"
              :items="defaultAreaItems"
              :rules="rules.area"
              :disabled="isDisable"
              clearable
              dense
              outlined
              :label="$t('categories.nonconformity.area')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('area', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <v-combobox
              v-model="nonConformity.event"
              :items="defaultEventItems"
              :rules="rules.event"
              :disabled="isDisable"
              clearable
              outlined
              dense
              :label="$t('categories.nonconformity.event')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('event', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <v-combobox
              v-model="nonConformity.other"
              :items="defaultOtherItems"
              :rules="rules.other"
              :disabled="isDisable"
              clearable
              outlined
              dense
              :label="$t('categories.nonconformity.other')"
            >
              <template #item="{ item }">
                <v-list-item-content>{{ item }}</v-list-item-content>
                <avs-deleteButton
                  @click.stop="deleteItem('other', item)"
                  :shape="{ text: true, small: true }"
                  :spantext="$t('categories.nonconformity.remove_list')"
                />
              </template>
            </v-combobox>

            <DatetimePicker
              v-model="nonConformity.report_date"
              :min="minReportDate"
              :max="maxReportDate"
              :readonly="!!isReadOnly"
              :disabled="isDisable"
              :label="$t('commons.date.report_date')"
              :textFieldProps="{
                placeholder: $t('commons.date.report_date'),
                rules: rules.report_date
              }"
            />

            <v-select
              v-model="nonConformity.status"
              :items="statusItems"
              :rules="rules.status"
              dense
              outlined
              :label="$t('commons.status.name')"
            ></v-select>

            <v-select
              v-model="nonConformity.criticality"
              :items="criticalityItems"
              :rules="rules.status"
              :disabled="isDisable"
              dense
              outlined
              :label="$t('commons.criticality')"
            ></v-select>

            <v-col class="pb-0 mb-0">
              <template v-if="isDisable">
                <v-row class="ml-1">
                  <div
                    v-for="(attachment, index) in nonConformity.attachments"
                    :key="index"
                    class="file-icon"
                  >
                    <a
                      :href="attachment.file_url"
                      target="_blank"
                      ><v-icon medium>mdi-file</v-icon></a
                    >
                  </div>
                </v-row>
              </template>
              <template v-else>
                <v-row class="d-flex align-center ml-1">
                  <!-- Bouton d'upload -->
                  <v-btn
                    outlined
                    color="primary"
                    dense
                    :disabled="isDisable"
                    @click="triggerFileUpload"
                    class="mr-3"
                  >
                    <v-icon left>mdi-upload</v-icon>
                    {{ $t("commons.attachments") }}
                  </v-btn>

                  <!-- Affichage des fichiers chargés -->
                  <div
                    v-for="(attachment, index) in nonConformity.attachments"
                    :key="index"
                    class="d-flex align-center mr-4"
                  >
                    <!-- Si le fichier a un lien existant -->
                    <template v-if="attachment.file_url">
                      <a
                        :href="attachment.file_url"
                        target="_blank"
                        class="d-flex align-center"
                      >
                        <v-icon
                          medium
                          class="mr-2"
                          >mdi-file</v-icon
                        >
                        {{ attachment.name }}
                      </a>
                    </template>
                    <!-- Si c'est un nouveau fichier -->
                    <template v-else>
                      <div class="d-flex align-center">
                        <v-icon
                          medium
                          class="mr-2"
                          >mdi-file</v-icon
                        >
                        {{ attachment.name }}
                      </div>
                    </template>
                    <v-btn
                      icon
                      small
                      color="red"
                      @click="removeFile(index)"
                      class="ml-2"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <!-- Input invisible pour le téléchargement -->
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    :disabled="isDisable"
                    @change="handleFilesChange"
                    style="display: none"
                  />
                </v-row>
              </template>
            </v-col>

            <v-row>
              <v-col class="pb-0 mb-0">
                <v-checkbox
                  :disabled="isDisable"
                  v-model="nonConformity.notify_oncreated"
                  @change="handleNotifyOnCreatedChange"
                >
                  <template v-slot:label>
                    <div>{{ $t("categories.inspection.send_notifications") }}</div>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col class="pb-0 mb-0">
                <v-checkbox
                  :disabled="isDisable"
                  v-model="nonConformity.notify_team_oncreated"
                  @change="handleNotifyTeamOnCreatedChange"
                >
                  <template v-slot:label>
                    <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
            <v-select
              v-if="nonConformity.notify_team_oncreated"
              :disabled="isDisable"
              v-model="nonConformity.users"
              :items="users"
              item-text="email"
              item-value="id"
              :label="$t('commons.send_to')"
              multiple
              chips
              outlined
              persistent-hint
            ></v-select>
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="2">
          <v-col
            cols="12"
            class="pt-0 d-flex justify-center"
          >
            <NonConformityPictures
              :validate-counter="validateRequest.pictures"
              @validated="v => (isFormValid.pictures = v)"
              v-model="nonConformity.pictures"
              :rules="rules.pictures"
              :inspection="nonConformity.inspection"
              :disabled="isReadOnly"
              :isDisable="isDisable"
            />
          </v-col>
        </v-stepper-content>

        <v-stepper-content step="3">
          <v-form
            ref="form-cause-and-resolution"
            v-model="isFormValid.causeAndResolution"
            class="pt-2"
            lazy-validation
          >
            <template>
              <div>
                <v-row class="p-3 ml-2">
                  <!-- 5 Why switch -->
                  <v-col cols="12" md="6" class="p-2">
                    <v-switch
                      v-model="nonConformity.use5Why"
                      :label="$t('commons.use_5_why')"
                      inset
                      color="success"
                    ></v-switch>
                  </v-col>
                </v-row>

                <div
                  v-if="nonConformity.use5Why"
                  class="why-wrapper"
                >
                  <div
                    v-for="(why, index) in nonConformity.whys"
                    :key="index"
                    dense
                    class="why-container"
                  >
                    <v-row
                      dense
                      class="mt-3 align-center "
                    >
                      <!-- Bouton pour supprimer -->
                      <v-col cols="1" class="d-flex align-center justify-center mb-3">
                        <v-btn
                          icon
                          color="red"
                          @click="removeWhy(index)"
                          :disabled="nonConformity.whys.length === 1"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>

                      <v-col cols="1" class="mb-3">
                        <span class="why-label">WHY - {{ index + 1 }} :</span>
                      </v-col>
                      <v-col cols="8">
                        <v-text-field
                          v-model="why.description"
                          :label="$t('commons.detailed_description')"
                          outlined
                          dense
                          class="why-input"
                          :rules="rules.whys"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="1">
                        <v-btn
                          class="why-action mb-5"
                          @click="openActionPopup(index)"
                          outlined
                        >
                          {{ $t("commons.action") }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Bouton pour ajouter un WHY -->
                  <v-row class="mt-4">
                    <v-col cols="12" class="d-flex justify-center">
                      <v-btn
                        @click="addWhy()"
                        :disabled="nonConformity.whys.length >= 5"
                        color="primary"
                      >
                        {{ $t("commons.add_why") }}
                      </v-btn>
                    </v-col>
                  </v-row>

                  
                </div>

                <v-row class="mt-2 p-3 ml-2">
                  <!-- 5 Why switch -->

                  <!-- RCA switch -->
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="nonConformity.useRca"
                      :label="$t('commons.use_rca')"
                      inset
                      color="success"
                    ></v-switch>
                  </v-col>
                </v-row>

                <div v-if="nonConformity.useRca">
                  <!-- Justification si on désactive 5 Why -->
                  <v-textarea
                    v-model="nonConformity.reasonNo5Why"
                    :label="$t('commons.reason_no_5why')"
                    :rules="rules.reasonNo5Why"
                    dense
                    outlined
                  ></v-textarea>
                  <v-text-field
                    v-model="nonConformity.due_to"
                    :rules="rules.due_to"
                    :disabled="isDisable"
                    auto-grow
                    clearable
                    dense
                    outlined
                    :label="$t('categories.nonconformity.due_to')"
                  />
                  <label>{{ $t("categories.nonconformity.details_cause") }}</label>
                  <ckeditor
                    :editor="editor"
                    :disabled="isDisable"
                    tag-name="textarea"
                    rows="3"
                    v-model="nonConformity.detailed_description"
                    :config="editorConfig"
                    :label="$t('categories.nonconformity.details_cause')"
                  ></ckeditor>
                  <label class="mt-2">{{ $t("categories.nonconformity.rca") }}</label>
                  <ckeditor
                    :editor="editor"
                    :disabled="isDisable"
                    tag-name="textarea"
                    rows="3"
                    v-model="nonConformity.root_cause_analysis"
                    :rules="rules.root_cause_analysis"
                    :config="editorConfig"
                    :label="$t('categories.nonconformity.rca')"
                  ></ckeditor>
                </div>
              </div>
            </template>
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="4">
          <v-layout
            class="d-flex justify-center"
            v-if="loading"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-layout>
          <template v-else>
            <NonConformityActionList
              :nonConformity="nonConformity"
              :projectId="projectId"
              v-if="nonConformityExists"
            />
          </template>
        </v-stepper-content>

        <v-stepper-content step="5">
          <NonConformitySign
            :nonConformity="nonConformity"
            :signatures="signatures"
            :isClosed="isClosed"
            @success="(v, m) => handleSuccess(v, 5, loadSignForm, m)"
            @error="handleError"
            @disableParent="handleSign"
            @cancel="handleClose"
            @confirm="handleConfirm"
          />
        </v-stepper-content>
      </template>
      <v-spacer></v-spacer>
      <template v-slot:actions>
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-left"
          @click="tab--"
          :disabled="tab <= 1"
          :name="$t('commons.previous')"
        />
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-right"
          right
          @click="tabIncrement"
          :name="nextLabel"
        />
      </template>
    </AvsSteppers>

    <avs-CloseDialog
      v-model="closeDialogIsOpen"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
    <v-dialog
      v-model="showActionPopup"
      max-width="500px"
    >
      <v-card v-if="selectedWhyIndex !== null && nonConformity.whys[selectedWhyIndex]">
        <v-card-title>{{ $t("commons.action_description") }}</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="nonConformity.whys[selectedWhyIndex].action"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeActionPopup">{{ $t("commons.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import _ from "lodash"
const noop = () => undefined
import { NonConformityService, PictureService, ProjectService } from "@/services"
import {
  NON_CONFORMITY_AREAS,
  STATUS_ITEMS,
  STATUS_ITEMS_DISABLED,
  CRITICALITY_ITEMS
} from "@/utils/consts"
import { mkNonConformity } from "@/utils/datamodels"

import { stringToDate } from "@/utils/datetime"
import { isInspector } from "@/utils/middleware"
import { TeamService } from "@/services"

import {
  // DateInput,
  InspectionInput,
  NonConformityPictures,
  NonConformityActionList
} from "@/components"
import { required, validateLength } from "@/utils/validator"

export default {
  name: "NonConformityCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    NonConformitySign: () => import("@/components/ri3/non-conformity/NonConformitySign.vue"),
    NonConformityPictures,
    NonConformityActionList,
    // DateInput,
    InspectionInput
  },
  props: {
    inDialog: {
      type: Boolean,
      default: false
    },
    inDialogParams: {
      type: Object,
      default: () => {}
    }
  },
  data: function () {
    return {
      cachedWhys: [],
      showActionPopup: false,
      selectedWhyIndex: null,
      NON_CONFORMITY_AREAS,
      nonConformityRef: null,
      nonConformity: mkNonConformity(),
      tab: this.$route.params.tab ? this.$route.params.tab : 1,
      nextLabel: this.$t("commons.next"), //NEXT.DEFAULT,
      loading: false,
      step: -1,
      minReportDate: null,
      maxReportDate: stringToDate(),
      selectedMethod: "5why", // "5why" ou "rca"
      reasonNo5Why: "",
      rules: {
        reasonNo5Why: [
          v =>
            !this.nonConformity.use5Why && (!v || v.trim() === "")
              ? this.$t("messages.error.justify_no_5why")
              : true
        ],
        whys: [required],
        inspection: [required],
        nc_number: [],
        quick_description: [],
        area: [required],
        event: [required],
        other: [],
        report_date: [required],
        status: [required],
        due_to: [],
        detailed_description: [],
        root_cause_analysis: [validateLength],
        corrective_action: [],
        preventive_action: [],
        pictures: []
      },
      isFormValid: {
        informations: false,
        pictures: false,
        causeAndResolution: false
      },
      validateRequest: {
        pictures: 0
      },
      areasData: {},
      signatures: undefined,
      isDisable: false,
      isClosed: false,
      closeDialogIsOpen: false,
      users: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [
          "paragraph",
          "heading1",
          "heading2",
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "undo",
          "redo",
          "-",
          "numberedList",
          "bulletedList"
        ]
      }
    }
  },
  created() {
    this.fetchNonConformity()
    this.fetchAreas()
    this.fetchUsers()
  },
  methods: {
    cleanWhys() {
      this.nonConformity.whys = this.nonConformity.whys.filter(
        why => why.description.trim() !== "" || why.action.trim() !== ""
      )
    },
    addWhy() {
      if (this.nonConformity.whys.length < 5) {
        const newWhy = { id: null, description: "", action: "", order: null }
        this.nonConformity.whys = [...this.nonConformity.whys, newWhy] // 🔥 Création d'un nouveau tableau
      } else {
        this.send(this.$t("messages.error.max_5_whys"), "warning")
      }
    },
    removeWhy(index) {
      if (this.nonConformity.whys.length > 1) {
        this.nonConformity.whys.splice(index, 1)
      } else {
        this.send(this.$t("messages.error.min_1_why"), "warning")
      }
    },
    toggle5Why() {
      if (this.nonConformity.use5Why) {
        if (this.cachedWhys.length > 0) {
          this.nonConformity.whys = _.cloneDeep(this.cachedWhys)
        } else {
          this.nonConformity.whys = [{ id: null, description: "", action: "", order: null }]
        }
      } else {
        this.cachedWhys = _.cloneDeep(this.nonConformity.whys)
        this.nonConformity.whys = []
      }
    },
    openActionPopup(index) {
      if (this.nonConformity.whys[index]) {
        this.selectedWhyIndex = index
        this.showActionPopup = true
      }
    },
    closeActionPopup() {
      this.showActionPopup = false
      this.selectedWhyIndex = null
    },
    // updateAction() {
    //   if (this.selectedWhyIndex !== null) {
    //     this.nonConformity.whys = [...this.nonConformity.whys] // Forcer Vue à détecter la mise à jour
    //   }
    // },
    // updateAction() {
    //   if (this.selectedWhyIndex !== null) {
    //     // Création d’un nouvel objet pour forcer la réactivité
    //     let updatedWhy = { ...this.nonConformity.whys[this.selectedWhyIndex] }
    //     updatedWhy.action = this.nonConformity.whys[this.selectedWhyIndex].action

    //     // 🔥 Utilisation de Vue.set pour assurer la réactivité
    //     this.$set(this.nonConformity.whys, this.selectedWhyIndex, updatedWhy)
    //   }
    // },
    deleteItem(type, value) {
      const params = { type, value }
      ProjectService.areas.delete(this, this.projectId, params).then(response => {
        this.fetchAreas()
      })
    },
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    handleInputOther() {
      this.$emit("input", this.nonConformity)
    },
    removeFile(index) {
      // Supprime le fichier sélectionné de la liste des attachments
      this.nonConformity.attachments.splice(index, 1)
      this.selectedFilesCount = this.nonConformity.attachments.length
      // Émet un événement pour mettre à jour les valeurs du formulaire
      this.handleInputOther()
    },
    handleFilesChange(e) {
      // Récupère les nouveaux fichiers ajoutés via l'input file
      const files = e.target.files || e.files

      // Boucle sur chaque nouveau fichier et le transforme en objet Blob
      Array.from(files).forEach(file => {
        const blob_obj = new Blob([file], { type: file.type })

        // Utilise FileReader pour lire le contenu du Blob
        const reader = new FileReader()
        reader.onload = () => {
          const blobObject = {
            file: reader.result, // Contenu du fichier lu (base64 ou texte)
            name: file.name, // Nom du fichier
            type: file.type, // Type MIME
            blob: blob_obj // Le Blob pour une utilisation directe
          }

          // Ajoute le nouvel objet Blob à la liste existante
          this.nonConformity.attachments.push(blobObject)

          // Émet un événement pour mettre à jour les valeurs du formulaire
          this.handleInputOther()
        }

        // Lire le fichier en base64
        reader.readAsDataURL(blob_obj)
      })

      // Met à jour le compteur des fichiers sélectionnés
      this.selectedFilesCount = this.nonConformity.attachments.length
    },
    save() {
      this.$refs["form-informations"].validate()
      this.validateRequest.pictures++
      this.$refs["form-cause-and-resolution"].validate()
      this.updateWhysOrder()
      this.nonConformity.whys = this.nonConformity.whys.map(why => ({
        id: why.id,
        description: why.description,
        order: why.order,
        action: why.action ? why.action : ""
      }))
      this.$nextTick(() => {
        this.nonConformity.lang = this.$i18n.locale
        if (
          this.isFormValid.informations &&
          this.isFormValid.pictures &&
          this.isFormValid.causeAndResolution
        )
          if (this.nonConformityExists) {
            this.updateNonConformity()
          } else {
            this.createNonConformity()
          }
        else {
          this.tab = 1
          const message = this.$t("messages.warning.all_required_info")
          this.send(message, "warning")
        }
      })
    },
    updateWhysOrder() {
      this.nonConformity.whys.forEach((why, index) => {
        why.order = index + 1
      })
    },
    tabIncrement() {
      return this.tab < 5 ? this.tab++ : this.$router.push({ name: "NonConformities" })
    },
    handleClose() {
      return this.$router.push({ name: "NonConformities" })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    getNonConformity(id) {
      if (id) {
        let t = this
        NonConformityService.get(this, this.projectId, id).then(r => {
          t.nonConformity = r
          t.nonConformityRef = _.cloneDeep(t.nonConformity)
          if (t.nonConformity.state === 4) {
            t.isDisable = true
          }
          if (this.nonConformity.use5Why && this.nonConformity.whys.length > 0) {
            this.cachedWhys = _.cloneDeep(this.nonConformity.whys)
          }
          if (t.nonConformity.status == "Closed") {
            t.isClosed = true
          }
        })
      }
    },
    createNonConformity() {
      this.cleanWhys()
      let tmp = _.cloneDeep(this.nonConformity)

      // We rename "file_url" to "file" for the create to work
      tmp.pictures = tmp.pictures.map(picture => {
        picture.file = picture.file_url
        delete picture.file_url
        return picture
      })

      this.loading = true
      NonConformityService.create(this, this.projectId, this.nonConformity.inspection.id, tmp)
        .then(response => {
          this.nonConformity = response
          this.nonConformityRef = _.cloneDeep(response)
          this.send(
            this.$t("messages.success.saved", {
              entity: this.$tc("categories.nonconformity.name", 1)
            }),
            "success"
          )
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.$store.commit("app/message", {
              msg: message,
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateNonConformity() {
      this.cleanWhys()
      const requests = []
      const picturesToCreate = []

      console.log("🚀 Payload avant envoi :", JSON.stringify(this.nonConformity.whys, null, 2))
      //  Create pictures that has no id
      //  Update pictures that were edited (has id)
      //  Delete pictures (has deleted==true)
      this.nonConformity.pictures.map(p => {
        if (p.id) {
          if (p.removed) {
            requests.push(PictureService.remove(this, p.id))
          } else {
            const refPicture = this.nonConformityRef.pictures.find(refP => refP.id === p.id)
            if (p.unassigned === true) {
              let obj = {
                non_conformity: this.nonConformity.id,
                name: p.name
              }
              if (p.file_url.startsWith("data")) {
                obj["file"] = p.file_url
              }
              requests.push(PictureService.update(this, p.id, obj))
            } else if (
              !_.isEqual(_.pick(p, ["name", "file_url"]), _.pick(refPicture, ["name", "file_url"]))
            ) {
              let updatedPic = _.pick(p, ["name", "file_url"])
              if (updatedPic.file_url != refPicture.file_url) {
                updatedPic["file"] = updatedPic["file_url"]
                delete updatedPic["file_url"]
              }
              requests.push(PictureService.update(this, p.id, updatedPic))
            }
          }
        } else {
          picturesToCreate.push(_.pick(p, ["name", "file_url"]))
        }
      })
      if (picturesToCreate.length > 0) {
        let mappedPictures
        mappedPictures = picturesToCreate.map(p => {
          return {
            name: p.name,
            file: p.file_url
          }
        })
        requests.push(
          PictureService.create(this, this.nonConformity.id, {
            files: mappedPictures
          })
        )
      }
      if (
        !_.isEqual(
          _.omit(this.nonConformity, ["pictures"]),
          _.omit(this.nonConformityRef, ["pictures"])
        )
      ) {
        this.updateWhysOrder()
        this.nonConformity.whys = [...this.nonConformity.whys]
        requests.push(
          NonConformityService.update(
            this,
            this.nonConformity.id,
            _.omit(this.nonConformity, ["pictures"])
          )
        )
      }
      if (requests.length > 0) {
        this.loading = true
        Promise.all(requests)
          .then(() => {
            this.send(
              this.$t("messages.success.updated", {
                entity: this.$tc("categories.nonconformity.name", 1)
              }),
              "success"
            )
          })
          .catch(err => {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.send(message, "error")
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.send(this.$t("messages.success.no_updated"), "info")
      }
    },
    fetchNonConformity() {
      // Load nonconformity
      this.getNonConformity(this.id)
    },
    fetchAreas() {
      // Load project/company areas
      ProjectService.areas.get(this, this.projectId).then(data => {
        this.areasData = data
      })
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    },
    handleSign() {
      // Handle message display
      this.isDisable = true
    },
    handleSuccess(isSaved, nextTab, postProcess = noop, message = null) {
      // Handle message display
      const msg_success = message ? message : this.$t("messages.success.operation")
      const data = isSaved
        ? { msg: msg_success, color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next display
      this.tab = nextTab

      // Postprocess
      postProcess()
    },
    handleConfirm() {
      NonConformityService.close(this, this.nonConformity.id)
        .then(r => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.closed", {
              entity: this.$tc("categories.nonconformity.name", 1)
            }),
            color: "success"
          })
          this.closeDialogIsOpen = false
          this.isClosed = true
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
      this.closeDialogIsOpen = false
    },
    handleCancel() {
      this.closeDialogIsOpen = false
    },
    async fetchNonConformitySignatures() {
      this.signatures = await NonConformityService.getSignatures(this.nonConformity.id)
    },
    async loadSignForm() {
      if (this.nonConformityExists) {
        this.loading = true
        this.fetchNonConformitySignatures().finally(() => {
          this.loading = false
        })
      }
    },
    handleNotifyOnCreatedChange() {
      if (this.nonConformity.notify_oncreated) {
        this.nonConformity.notify_team_oncreated = false
        this.nonConformity.users = []
      }
    },
    handleNotifyTeamOnCreatedChange() {
      if (this.nonConformity.notify_team_oncreated) {
        this.nonConformity.notify_oncreated = false
      } else {
        this.nonConformity.users = []
      }
    },
    fetchUsers() {
      TeamService.getTeam(this, this.projectId).then(response => {
        this.users = response
      })
    }
  },
  watch: {
    tab(newValue, oldValue) {
      if (oldValue === 1 && newValue === 2) {
        this.tab = this.$refs["form-informations"].validate() ? 2 : 1
      } else if (oldValue === 2 && newValue === 3) {
        this.tab = this.isFormValid.pictures ? 3 : 2
        this.validateRequest.pictures++
      } else if (oldValue === 3 && newValue === 4) {
        this.save()
      }

      // Set Save label
      if (newValue === 3) {
        this.nextLabel = this.$t("commons.save_next") //NEXT.SAVE
      } else if (newValue === 5) {
        this.nextLabel = this.$t("commons.finish") //NEXT.FINISH
      } else if (newValue === 4) {
        this.nextLabel = this.$t("commons.next")
      } else {
        this.loadSignForm()
        this.nextLabel = this.$t("commons.next") //NEXT.DEFAULT
      }
    },
    "nonConformity.inspection": {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.minReportDate = stringToDate(newValue.start_date)
        }
      },
      deep: true
    }
    // "nonConformity.use5Why"(newValue) {
    //   if (newValue) {
    //     if (this.cachedWhys.length > 0) {
    //       this.nonConformity.whys = [...this.cachedWhys]
    //     } else if (this.nonConformity.whys.length === 0) {
    //       this.nonConformity.whys = [{ id: null, description: "", action: "", order: null }]
    //     }
    //   } else {
    //     this.cachedWhys = [...this.nonConformity.whys]
    //     this.nonConformity.whys = []
    //   }
    // }
    // "nonConformity.use5Why"(newValue) {
    //   if (newValue && this.nonConformity.whys.length === 0) {
    //     this.nonConformity.whys = [{ id: null, description: "", action: "", order: null }]
    //   } else if (!newValue) {
    //     this.nonConformity.whys = []
    //   }
    // }
  },
  computed: {
    defaultAreaItems() {
      return Object.keys(this.areasData)
    },
    defaultEventItems() {
      const eventsData = this.areasData[this.nonConformity.area] ?? {}
      return Object.keys(eventsData)
    },
    defaultOtherItems() {
      const eventsData = this.areasData[this.nonConformity.area] ?? {}
      return eventsData[this.nonConformity.event] ?? []
    },
    statusItems() {
      return isInspector(this.projectRole) ? STATUS_ITEMS : STATUS_ITEMS_DISABLED
    },
    criticalityItems() {
      return isInspector(this.projectRole) ? CRITICALITY_ITEMS : CRITICALITY_ITEMS
    },
    id() {
      return this.inDialog ? this.inDialogParams?.id : this.$route.params.id || null
    },
    inspection() {
      return this.$route.query.inspection || this.$route.params.id || null
    },
    // A non-conformity is read-only if its closed_date is null for operators
    isReadOnly() {
      return !!this.nonConformity.closed_at && !isInspector(this.projectRole)
    },
    nonConformityExists() {
      return this.nonConformity.id > 0
    },
    projectId() {
      return this.$store.state.app.project.id
    },
    projectRole() {
      return this.$store.state.app.project.role
    }
  }
}
</script>

<style lang="scss">
.custom-container {
  display: flex;
  flex-direction: column;
  height: 80vh;
}
.stepper-item {
  flex: 1 1 auto;
  overflow-y: auto;
}
.ck.ck-editor__main > .ck-editor__editable {
  height: 130px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}

.why-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.why-container {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 15px;
}
.why-label {
  width: 100px;
  font-weight: bold;
  text-align: right;
}
.why-input {
  flex-grow: 1;
}
.why-action {
  min-width: 120px;
}
// .why-action {
//   min-width: 100px;
// }
</style>
